import React, { useState } from 'react';
import './OnboardingOptionCard.css';

// A long list (the 36 A-levels) keeps two columns on a phone, where a short
// one drops to one: one column would be 36 rows of scrolling.
export const MANY_OPTIONS = 12;

function optsClassName(step) {
  const grid = step.grid ? ' grid' : '';
  const many = step.grid && (step.options || []).length > MANY_OPTIONS ? ' many' : '';
  return `onboarding-opts${grid}${many}`;
}

// Renders one onboarding step as an interactive card, matching
// docs/design/ivori-chat-prototype.html's .card/.opts/.opt system exactly (single-select,
// multi-select with grid, free-text). No "Other" custom-entry support —
// none of the three multi-select steps in onboarding_steps.py set
// `other: true`, and validate_answer only accepts fixed option codes today
// (see docs/plans/ONBOARDING_PLAN.md's Phase 1 scope notes).
//
// Built against mocked step data for this stage — no network calls here.
// The caller (ChatShell, wired in a later stage) is responsible for
// fetching the real step catalogue/state and calling onAnswer(value) with
// the submitted value once the user acts.
//
// step shape (matches backend/app/content/onboarding_steps.py exactly):
//   { id, kind: 'single'|'multi'|'text', prompt: [string], options?: [{v, l}],
//     grid?: bool, placeholder?: string, cta?: string }
// stepIndex/totalSteps: for the progress pips (card-head), matching the
// prototype's cardHead(step, index, total).
// initialValue: the step's current answer, if any (edit mode — see
// OnboardingSummaryCard.js). Single-select marks the matching option 'on';
// multi-select seeds the selection from it; text seeds the input. Absent
// (undefined) in the normal first-time-answering flow, which behaves
// exactly as before.
// onSelectionChange(codes): optional; called with the new multi-select
// selection on every toggle. Edit mode (OnboardingSummaryCard.js) keeps its
// draft in step with the toggles through it, so "Save answers" saves them
// without the card's own Continue being pressed. Continue and onAnswer are
// unchanged.

function CardHead({ kind, stepIndex, totalSteps }) {
  const pips = [];
  for (let i = 0; i < totalSteps; i++) {
    pips.push(
      <span
        key={i}
        className={`onboarding-pip-step${i < stepIndex ? ' done' : i === stepIndex ? ' now' : ''}`}
      />
    );
  }
  return (
    <div className="onboarding-card-head">
      <div className="onboarding-progress">{pips}</div>
      {kind === 'multi' && (
        <div className="onboarding-legend many">
          <span className="onboarding-legend-glyph" />
          Pick as many as apply
        </div>
      )}
      {kind === 'single' && (
        <div className="onboarding-legend one">
          <span className="onboarding-legend-glyph" />
          Pick one
        </div>
      )}
    </div>
  );
}

function OnboardingOptionCard({ step, stepIndex, totalSteps, onAnswer, initialValue, onSelectionChange }) {
  const [multiSelected, setMultiSelected] = useState(
    step.kind === 'multi' && Array.isArray(initialValue) ? initialValue : []
  );
  const [textValue, setTextValue] = useState(
    step.kind === 'text' && typeof initialValue === 'string' ? initialValue : ''
  );

  const toggleMultiOption = (code) => {
    const next = multiSelected.includes(code)
      ? multiSelected.filter((c) => c !== code)
      : [...multiSelected, code];
    setMultiSelected(next);
    if (onSelectionChange) onSelectionChange(next);
  };

  const handleSingleSelect = (code) => {
    onAnswer(code);
  };

  const handleMultiSubmit = () => {
    if (multiSelected.length === 0) return;
    onAnswer(multiSelected);
  };

  const handleTextSubmit = (e) => {
    e.preventDefault();
    const v = textValue.trim();
    if (!v) return;
    onAnswer(v);
  };

  return (
    <div className="onboarding-card">
      <CardHead kind={step.kind} stepIndex={stepIndex} totalSteps={totalSteps} />

      {step.kind === 'text' && (
        <div className="onboarding-card-body">
          <form className="onboarding-other-field onboarding-other-field-open" onSubmit={handleTextSubmit}>
            <input
              type="text"
              placeholder={step.placeholder || ''}
              aria-label={step.placeholder || 'Answer'}
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              autoFocus
            />
            <button type="submit" className="onboarding-btn-add" disabled={!textValue.trim()}>
              {step.cta || 'Save'}
            </button>
          </form>
        </div>
      )}

      {step.kind === 'single' && (
        <div className="onboarding-card-body">
          <div className={optsClassName(step)}>
            {(step.options || []).map((opt) => (
              <button
                key={opt.v}
                type="button"
                className={`onboarding-opt${opt.v === initialValue ? ' on' : ''}`}
                data-kind="single"
                onClick={() => handleSingleSelect(opt.v)}
              >
                <span className="onboarding-mark">
                  <span className="onboarding-pip" />
                </span>
                <span>{opt.l}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {step.kind === 'multi' && (
        <>
          <div className="onboarding-card-body">
            <div className={optsClassName(step)}>
              {(step.options || []).map((opt) => {
                const isSelected = multiSelected.includes(opt.v);
                return (
                  <button
                    key={opt.v}
                    type="button"
                    className={`onboarding-opt${isSelected ? ' on' : ''}`}
                    data-kind="multi"
                    onClick={() => toggleMultiOption(opt.v)}
                  >
                    <span className="onboarding-mark">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    <span>{opt.l}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="onboarding-card-foot">
            <div className="onboarding-count">
              {multiSelected.length === 0 ? 'Nothing selected yet' : `${multiSelected.length} selected`}
            </div>
            <button
              type="button"
              className="onboarding-btn-primary"
              onClick={handleMultiSubmit}
              disabled={multiSelected.length === 0}
            >
              Continue
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default OnboardingOptionCard;
